import React from 'react';
import { Flex, Modal, Result, Spin, theme } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useGetDomainQuery } from '../integrationWizard/api/integrationWizardSlice';
import { useSelector } from '../../hooks/usePrerenderUser';
import RenderProofEntryPointBanner from './components/RenderProofEntryPointBanner';
import RenderProofScreen from './components/RenderProofScreen';
import { SCREEN_READER_ONLY } from './ScreenReaderOnly';
import { daysSinceIntegration, proofDomain } from './RenderProofAnalyticsEvents';
import { hasReportableProof } from './RenderProofDisplay';
import { useRenderProofExperience } from './useRenderProofExperience';
import { useRenderProof } from './useRenderProof';
import { useRenderProofEntryPoint } from './useRenderProofEntryPoint';
import { useRenderProofExperimentProfile } from './useRenderProofExperimentProfile';
import { useTrackRenderProofEvent } from './useTrackRenderProofEvent';

const SITEMAPS_PATH = '/sitemaps';

// FIXME: no exact token — wide enough that the proof's two lines of copy each
// stay on one line at desktop widths; below this the browser wraps them.
const PROOF_MODAL_WIDTH = 900;

function RenderProofEntryPoint() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  // Runs for every dashboard visitor, including the control arm and accounts
  // without the flag — they are the comparison group and have to be labelled.
  useRenderProofExperimentProfile();

  const { isCandidate, isCandidateNow, markViewed, dismiss } =
    useRenderProofEntryPoint();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  // Sticky: the proof retires the banner, so this component would otherwise
  // unmount the moment the modal closes — before antd runs its close lifecycle,
  // and taking the focus anchor with it.
  const [hasOpenedProof, setHasOpenedProof] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Which domain to prove is a cheap read — two selects, no writes — so it runs
  // as soon as the account is a candidate. The banner then waits for it: an
  // offer to show the proof for a domain we cannot name opens a modal that can
  // never resolve, and closing it leaves the banner to be clicked again.
  const { data: domainData } = useGetDomainQuery(null, { skip: !isCandidate });
  const domain = domainData?.domain;

  // The proof itself waits for the modal. GET /v3/render-proof is not a read:
  // it queues the homepage render when none exists, and renders are billable.
  // Running it for every candidate on dashboard load would spend a customer's
  // quota for fourteen days on a banner they never clicked.
  //
  // Kept alive by `isModalOpen` rather than candidacy: viewing the proof marks
  // the account served, which withdraws candidacy, and a skipped query drops
  // `currentData` — blanking the proof out of the modal the moment it was
  // counted as viewed.
  const { proof, isFetching, isError, isStillRendering } = useRenderProof({
    domain,
    skip: !isModalOpen,
  });
  const showProof = hasReportableProof(proof);
  // Captured before `showProof` narrows `proof` away: inside a branch where the
  // guard is false, TypeScript knows only that there is nothing showable.
  const proofStatus = proof?.status;
  const shouldShow = isCandidate && domain !== undefined;

  const experience = useRenderProofExperience();
  const trackingCodeInstalledDate = useSelector(
    (state) => state.prerenderUser.trackingCodeInstalledDate,
  );
  const trackRenderProofEvent = useTrackRenderProofEvent();
  const proofViewedTrackedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isModalOpen || !showProof || proofViewedTrackedRef.current) {
      return;
    }
    // A proof that resolves after the window has closed is not an exposure this
    // experiment gets to claim.
    if (!isCandidateNow()) return;
    proofViewedTrackedRef.current = true;
    // Marked here, not when the banner is clicked: the account has been served
    // only once it has actually been shown something. Marking on the click
    // spent the entry point on a modal that might hold nothing but a spinner.
    markViewed();
    trackRenderProofEvent('Integration: Render Proof Viewed', {
      location: 'dashboard',
      // Taken from the proof, which is present whenever this fires, rather than
      // the separately-cached domain, which can be empty.
      domain: proofDomain(proof.url, domain ?? ''),
      render_proof_status: proof.status,
      render_status_code: proof.renderStatusCode,
      days_since_integration: daysSinceIntegration(
        trackingCodeInstalledDate,
        new Date(),
      ),
      render_proof_experience: experience,
    });
  }, [
    isModalOpen,
    showProof,
    proof,
    domain,
    trackingCodeInstalledDate,
    experience,
    trackRenderProofEvent,
    markViewed,
    isCandidateNow,
  ]);

  // Someone clicked the banner and got an apology. Tracked for the same reason
  // as the wizard's case: absence has to be distinguishable from indifference.
  const unavailableTrackedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isModalOpen || showProof || isFetching || isStillRendering) return;
    if (!isError && proofStatus === undefined) return;
    if (unavailableTrackedRef.current) return;

    unavailableTrackedRef.current = true;
    trackRenderProofEvent('Integration: Render Proof Unavailable', {
      location: 'dashboard',
      domain: domain ?? '',
      // Both awaiting statuses can only reach this effect once the polling
      // window has closed, so they are timeouts: an `origin_error` seen here is
      // necessarily status-less (one with a status code shows the warning
      // screen instead of landing in this apology).
      reason: isError
        ? 'error'
        : proofStatus === 'pending' || proofStatus === 'origin_error'
          ? 'timeout'
          : 'no_comparable_data',
      days_since_integration: daysSinceIntegration(
        trackingCodeInstalledDate,
        new Date(),
      ),
      render_proof_experience: experience,
    });
  }, [
    isModalOpen,
    showProof,
    isFetching,
    isStillRendering,
    isError,
    proofStatus,
    domain,
    trackingCodeInstalledDate,
    experience,
    trackRenderProofEvent,
  ]);

  const dismissProof = () => {
    trackRenderProofEvent('Integration: Render Proof Dismissed', {
      location: 'dashboard',
      days_since_integration: daysSinceIntegration(
        trackingCodeInstalledDate,
        new Date(),
      ),
      render_proof_experience: experience,
    });
    dismiss();
  };

  const onTroubleshootStatusCode = () => {
    trackRenderProofEvent('Status codes doc viewed', {
      source: 'render_proof',
      source_location: 'dashboard',
    });
  };

  const openProof = () => {
    // The window may have closed while this page sat idle, which would not have
    // re-rendered the banner away.
    if (!isCandidateNow()) return;
    setHasOpenedProof(true);
    setIsModalOpen(true);
  };

  const closeProof = () => {
    setIsModalOpen(false);
  };

  // Viewing the proof retires the banner, so by the time the modal closes the
  // button that opened it is gone and antd's default restore would drop
  // keyboard and screen-reader users onto <body>. Focus goes to the container
  // instead — but only in that case: while the trigger still exists, returning
  // focus to it is the better behaviour and antd already does it.
  const restoreFocus = () => {
    if (shouldShow) return;
    containerRef.current?.focus();
  };

  const onAddSitemap = () => {
    trackRenderProofEvent('Sitemap page viewed', {
      source: 'render_proof',
      source_location: 'dashboard',
    });
    closeProof();
    navigate(SITEMAPS_PATH);
  };

  const onGoToDashboard = () => {
    trackRenderProofEvent('Dashboard viewed', {
      source: 'render_proof',
      source_location: 'dashboard',
    });
    closeProof();
  };

  // Viewing the proof marks the account served, which withdraws candidacy — so
  // the banner goes away, but the modal has to stay mounted until it is closed,
  // and the container outlives both as somewhere for focus to land.
  if (!shouldShow && !isModalOpen && !hasOpenedProof) return null;

  return (
    <Flex
      vertical
      ref={containerRef}
      tabIndex={-1}
      style={shouldShow ? { marginBottom: token.margin } : undefined}
    >
      {shouldShow && (
        <RenderProofEntryPointBanner
          onView={openProof}
          onDismiss={dismissProof}
        />
      )}
      {hasOpenedProof && (
      <Modal
        open={isModalOpen}
        onCancel={closeProof}
        // The design has no header, which would leave the dialog with no
        // accessible name. antd does not forward `aria-label` to the dialog
        // element, so the name has to come from `title` — hidden visually, and
        // with the header's spacing removed so the layout is unchanged.
        title={<span style={SCREEN_READER_ONLY}>Your first rendered page</span>}
        styles={{ header: { marginBottom: 0 } }}
        focusTriggerAfterClose={shouldShow}
        afterClose={restoreFocus}
        footer={null}
        width={PROOF_MODAL_WIDTH}
        destroyOnHidden
      >
        {isFetching && <Spin size="large" style={{ width: '100%' }} />}

        {!isFetching && showProof && (
          <RenderProofScreen
            proof={proof}
            onAddSitemap={onAddSitemap}
            onGoToDashboard={onGoToDashboard}
            onTroubleshootStatusCode={onTroubleshootStatusCode}
            // Not "Close": the dialog already has an X with that name.
            goToDashboardLabel="Done"
          />
        )}

        {!isFetching && !showProof && (
          <Result
            status={isError ? 'warning' : 'info'}
            title={
              isError
                ? 'We could not load your render proof'
                : 'Your homepage is still being rendered'
            }
            subTitle={
              isError
                ? 'Please try again in a few minutes.'
                : isStillRendering
                  ? 'This updates on its own as soon as the render is ready.'
                  : 'Check back shortly to see what crawlers now receive.'
            }
          />
        )}
      </Modal>
      )}
    </Flex>
  );
}

export default RenderProofEntryPoint;
